import React, { useState, forwardRef } from 'react'
import './button.scss'
import clsx from 'clsx'
import { CursorIcon } from '@/shared/assets'
import { cn } from '@/true-fsd/shared/utils/css'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    children: React.ReactNode
    className?: string
    wrapperClassName?: string
    variant?: 'primary' | 'secondary' | 'error'
    size?: 'small' | 'medium' | 'large' | 'fit'
    disabled?: boolean
    iconLeft?: boolean
    iconRight?: boolean
    tooltipText?: string
    iconSrc?: string
    iconComponent?: React.ComponentType<{ className?: string }>
    disabledBySubscriptionStatus?: boolean
    tooltipStyle?: string
    visuallyDisabled?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            children,
            variant = 'primary',
            size = 'medium',
            className,
            wrapperClassName,
            disabled,
            iconLeft,
            iconRight,
            tooltipText,
            iconSrc,
            iconComponent: IconComponent,
            disabledBySubscriptionStatus = false,
            tooltipStyle = '',
            visuallyDisabled = false,
            ...props
        },
        ref
    ) => {
        const [isHovered, setIsHovered] = useState(false)

        return disabledBySubscriptionStatus ? (
            <div
                className={clsx('button__wrapper', wrapperClassName)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                    cursor: `url(${CursorIcon}), auto !important ${visuallyDisabled ? 'not-allowed' : 'pointer'}`,
                    opacity: visuallyDisabled ? 0.5 : 1,
                }}
            >
                <button
                    ref={ref}
                    className={clsx(
                        'button',
                        `button__${variant}`,
                        `button__${size}`,
                        { 'button--disabled': disabled || visuallyDisabled },
                        className
                    )}
                    disabled={disabled || disabledBySubscriptionStatus}
                    {...props}
                >
                    {iconLeft && IconComponent && (
                        <IconComponent className="button__icon-left" />
                    )}
                    {iconLeft && iconSrc && (
                        <img
                            src={iconSrc}
                            className={clsx(
                                `button__icon-left:${disabled ?? ':disabled'}`
                            )}
                        />
                    )}
                    {children}
                    {iconRight && IconComponent && (
                        <IconComponent className="button__icon-right" />
                    )}
                    {iconRight && iconSrc && (
                        <img src={iconSrc} className="button__icon-right" />
                    )}
                </button>
                {isHovered && tooltipText && (
                    <div className={cn('button__tooltip', tooltipStyle)}>
                        <div className="button__tooltip-content">
                            {tooltipText}
                        </div>
                        <div className="button__tooltip-pointer" />
                    </div>
                )}
            </div>
        ) : (
            <button
                ref={ref}
                className={clsx(
                    'button',
                    `button__${variant}`,
                    `button__${size}`,
                    { 'button--disabled': disabled || visuallyDisabled },
                    className
                )}
                style={{
                    opacity: visuallyDisabled ? 0.5 : 1,
                    cursor: visuallyDisabled ? 'not-allowed' : 'pointer',
                }}
                disabled={disabled}
                {...props}
            >
                {iconLeft && IconComponent && (
                    <IconComponent className="button__icon-left" />
                )}
                {iconLeft && iconSrc && (
                    <img src={iconSrc} className="button__icon-left" />
                )}
                {children}
                {iconRight && IconComponent && (
                    <IconComponent className="button__icon-right" />
                )}
                {iconRight && iconSrc && (
                    <img src={iconSrc} className="button__icon-right" />
                )}
            </button>
        )
    }
)
